// Формулы (агент math): выключные (номер справа, экспликация «где», перенос) и строчные; правка на месте
// (MathLive, как в Word: Alt+=), окно редактора с палитрами, вставка из Word/MathML/LaTeX/линейной записи,
// приведение к СТО (4.6), живые подсказки, вкладка ленты «Формулы». Экспорт - stoweb/convert/nodes_math.py.
import './math.css';
import type { RegistryApi, Ctx } from '../../core/registry';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { insertBlocks } from '../../core/editor/insert';
import { FormulaView, MathInlineView, replaceFormula } from './views';
import { FormulaDialog, type FormulaResult } from './FormulaDialog';
import { WhereDialog, ImportDialog, HelpDialog, type ImportResult } from './dialogs';
import { setupRibbon } from './ribbon';
import { setupPaste, MathInputExtension, insertLatex } from './paste';
import { setupLint } from './lint';
import { mathUI } from './state';
import { renderTex, toMathML } from './ml';
import * as L from './latex';
import * as D from './detect';
import * as O from './omml';
import { convertLinear } from './unicodemath';

export { renderTex };

/** Узел формулы под выделением (NodeSelection) или у курсора. */
function selectedFormula(ctx: Ctx): { pos: number; node: any } | null {
  const ed = ctx.editor;
  if (!ed) return null;
  const sel = ed.state.selection;
  if (sel instanceof NodeSelection && ['formula', 'mathInline'].includes(sel.node.type.name)) return { pos: sel.from, node: sel.node };
  return null;
}

/** Alt+= : новая формула на месте (или выделенный текст -> формула). */
function quick(ctx: Ctx, kind?: unknown) {
  const ed = ctx.editor;
  if (!ed) return;
  const sel = ed.state.selection;
  if (!kind && !sel.empty && !(sel instanceof NodeSelection)) { convertSelection(ctx); return; }
  const cur = selectedFormula(ctx);
  if (cur && !kind) { mathUI.requestEdit(cur.pos); return; }
  const $f = sel.$from;
  const emptyPara = $f.parent.type.name === 'paragraph' && $f.parent.content.size === 0;
  const block = kind === 'block' || kind === 'calc' || (!kind && emptyPara) || (kind !== 'inline' && $f.parent.type.name !== 'paragraph');
  if (block) {
    const at = insertBlocks(ed, { type: 'formula', attrs: { latex: '', numbered: kind !== 'calc', where: [] } });
    mathUI.requestEdit(at);
  } else {
    const at = sel.from;
    ed.chain().focus().insertContent({ type: 'mathInline', attrs: { latex: '' } }).run();
    mathUI.requestEdit(at);
  }
}

/** Выделенный текст («x^2/(y+1)», «a*b», LaTeX, линейная запись) -> формула; весь абзац -> выключная. */
function convertSelection(ctx: Ctx) {
  const ed = ctx.editor;
  if (!ed) return;
  const { from, to, $from } = ed.state.selection;
  const text = ed.state.doc.textBetween(from, to, ' ');
  if (!text.trim()) { quick(ctx, 'inline'); return; }
  const r = D.anyToLatex(text, D.detectFormat(text) === 'text' ? 'linear' : undefined);
  if (!r.latex) return;
  const whole = $from.parent.type.name === 'paragraph' && from === $from.start() && to === $from.end();
  if (whole) {
    const s = $from.before();
    ed.chain().focus().insertContentAt({ from: s, to: $from.after() }, { type: 'formula', attrs: { latex: r.latex, numbered: true, where: [] } }).run();
  } else {
    ed.chain().focus().insertContentAt({ from, to }, { type: 'mathInline', attrs: { latex: r.latex } }).run();
  }
  ctx.toast('Текст преобразован в формулу (' + D.FORMAT_NAMES[r.format] + ')', 'ok', 2500);
}

/** Окно редактора формулы (кнопка «Формула»): новая формула у курсора. */
async function openNew(ctx: Ctx, extra: Record<string, unknown> = {}) {
  const ed = ctx.editor;
  if (!ed) return;
  const $f = ed.state.selection.$from;
  const r = await ctx.openDialog<FormulaResult>('math.edit', { latex: '', numbered: true, where: [], display: true, ...extra });
  if (!r || !r.latex) return;
  // вставляем туда, где был курсор до открытия окна
  if (r.display) insertBlocks(ed, { type: 'formula', attrs: { latex: r.latex, numbered: r.numbered, where: r.where } });
  else {
    try { ed.view.dispatch(ed.state.tr.setSelection(TextSelection.create(ed.state.doc, Math.min($f.pos, ed.state.doc.content.size)))); } catch { /* */ }
    ed.chain().focus().insertContent({ type: 'mathInline', attrs: { latex: r.latex } }).run();
  }
}

async function editWhereSelected(ctx: Ctx) {
  const ed = ctx.editor;
  const cur = selectedFormula(ctx);
  if (!ed || !cur || cur.node.type.name !== 'formula') { ctx.toast('Выделите формулу (щелчок по номеру или стрелками)', 'info', 2500); return; }
  const r = await ctx.openDialog<any[]>('math.where', { latex: cur.node.attrs.latex, where: cur.node.attrs.where || [], pos: cur.pos, autofill: true });
  if (!r) return;
  const n = ed.state.doc.nodeAt(cur.pos);
  if (n) ed.view.dispatch(ed.state.tr.setNodeMarkup(cur.pos, undefined, { ...n.attrs, where: r }));
}

/** Все формулы документа -> приведение к СТО. */
function canonAll(ctx: Ctx) {
  const ed = ctx.editor;
  if (!ed) return;
  let tr = ed.state.tr;
  let n = 0;
  ed.state.doc.descendants((node, pos) => {
    if (node.type.name === 'formula' || node.type.name === 'mathInline') {
      const c = L.canon(node.attrs.latex || '').latex;
      if (c !== node.attrs.latex) { tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, latex: c }); n++; }
      return false;
    }
    return true;
  });
  if (n) ed.view.dispatch(tr);
  ctx.toast(n ? `Исправлено формул: ${n}` : 'Все формулы уже оформлены по СТО', n ? 'ok' : 'info', 2500);
}

/** Формула-картинка -> набрать формулой (рисунок заменяется формулой). */
async function retypeFigure(ctx: Ctx, pos: number) {
  const ed = ctx.editor;
  if (!ed) return;
  const node = ed.state.doc.nodeAt(pos);
  if (!node || node.type.name !== 'figure') return;
  const pid = ctx.store.getState().projectId;
  const image = node.attrs.src && pid ? `/api/projects/${encodeURIComponent(pid)}/assets/${encodeURIComponent(node.attrs.src)}` : undefined;
  const r = await ctx.openDialog<FormulaResult>('math.edit', { latex: '', numbered: true, where: [], display: true, image, title: 'Набрать формулу вместо картинки' });
  if (!r || !r.latex) return;
  const cur = ed.state.doc.nodeAt(pos);
  if (!cur || cur.type.name !== 'figure') return;
  const repl = r.display ? ed.schema.nodes.formula.create({ latex: r.latex, numbered: r.numbered, where: r.where })
    : ed.schema.nodes.paragraph.create(null, ed.schema.nodes.mathInline.create({ latex: r.latex }));
  ed.view.dispatch(ed.state.tr.replaceWith(pos, pos + cur.nodeSize, repl));
  ctx.toast('Картинка заменена формулой', 'ok', 2500);
}

async function copyText(ctx: Ctx, text: string, what: string) {
  try { await navigator.clipboard.writeText(text); ctx.toast('Скопировано: ' + what, 'ok', 1500); } catch { ctx.toast('Не удалось скопировать', 'warn'); }
}

const stopInside = ({ event }: { event: Event }) => {
  const t = event.target as HTMLElement | null;
  return !!t && !!t.closest?.('math-field, input, textarea, select, .mx-bar, .mx-inline-bar, .mx-brk-menu');
};

export default function setup(r: RegistryApi) {
  // --- команды
  r.commands.add({ id: 'math.formula', title: 'Формула', description: 'Окно редактора формулы: палитры, вставка из Word/LaTeX/MathML, экспликация (4.6)', icon: 'formula',
    run: (c, arg) => openNew(c, (arg as any) || {}) });
  r.commands.add({ id: 'math.quick', title: 'Новая формула', description: 'Формула на месте, как в Word: в пустом абзаце - с номером, в тексте - в строке', icon: 'formula',
    shortcut: 'Alt-=', run: (c, arg) => quick(c, arg) });
  r.commands.add({ id: 'math.inline', title: 'Формула в строке', icon: 'sigma', shortcut: 'Mod-Alt-=', run: (c) => quick(c, 'inline') });
  r.commands.add({ id: 'math.toggleNumber', title: 'Нумеровать формулу', icon: 'hash',
    isActive: (c) => { const f = selectedFormula(c); return !!f && f.node.type.name === 'formula' && !!f.node.attrs.numbered; },
    isEnabled: (c) => { const f = selectedFormula(c); return !!f && f.node.type.name === 'formula'; },
    run: (c) => {
      const f = selectedFormula(c);
      if (!f || f.node.type.name !== 'formula') return;
      c.editor!.view.dispatch(c.editor!.state.tr.setNodeMarkup(f.pos, undefined, { ...f.node.attrs, numbered: !f.node.attrs.numbered }));
    } });
  r.commands.add({ id: 'math.where', title: 'Экспликация «где»', description: 'Пояснение символов формулы (4.6.1.7)', icon: 'list', run: (c) => editWhereSelected(c) });
  r.commands.add({ id: 'math.fromText', title: 'Формула из текста', description: 'LaTeX, формула Word, MathML, линейная запись, AsciiMath', icon: 'paste',
    run: async (c) => {
      const res = await c.openDialog<ImportResult>('math.import', {});
      if (res) insertLatex(c, res.latex, res.mode !== 'inline', res.mode === 'num');
    } });
  r.commands.add({ id: 'math.convertSelection', title: 'Текст в формулу', icon: 'type', run: (c) => convertSelection(c) });
  r.commands.add({ id: 'math.chem', title: 'Химическая формула', icon: 'omega', run: (c) => openNew(c, { pal: 'chem', title: 'Химическая формула (4.6.3)' }) });
  r.commands.add({ id: 'math.canonAll', title: 'Привести формулы к СТО', description: 'Десятичная запятая, знаки умножения, функции и константы прямым шрифтом во всех формулах', icon: 'check',
    run: (c) => canonAll(c) });
  r.commands.add({ id: 'math.help', title: 'Как набирать формулы', icon: 'info', run: (c) => { void c.openDialog('math.help'); } });

  // --- лента, окна, отображение
  setupRibbon(r);
  r.dialogs.add({ id: 'math.edit', component: FormulaDialog });
  r.dialogs.add({ id: 'math.where', component: WhereDialog });
  r.dialogs.add({ id: 'math.import', component: ImportDialog });
  r.dialogs.add({ id: 'math.help', component: HelpDialog });
  r.nodeViews.add({ id: 'formula', component: FormulaView, options: { stopEvent: stopInside } });
  r.nodeViews.add({ id: 'mathInline', component: MathInlineView, options: { as: 'span', stopEvent: stopInside } });
  r.extensions.add({ id: 'math.input', order: 50, create: () => MathInputExtension });

  // --- контекстное меню
  r.contextMenu.add({ id: 'math', order: 30, items: (ctx, t) => {
    const f = [...t.nodes].reverse().find((n) => n.type === 'formula' || n.type === 'mathInline');
    const fig = [...t.nodes].reverse().find((n) => n.type === 'figure');
    if (f) {
      const latex = f.attrs.latex || '';
      const display = f.type === 'formula';
      return [
        { id: 'math-edit', label: 'Изменить формулу…', icon: 'formula', onClick: async (c) => {
          const r2 = await c.openDialog<FormulaResult>('math.edit', { latex, numbered: display ? f.attrs.numbered : true, where: display ? f.attrs.where || [] : [], display, pos: f.pos });
          if (r2) replaceFormula(c.editor, f.pos, f.type, r2);
        } },
        ...(display ? [
          { id: 'math-num', label: 'С номером', checked: !!f.attrs.numbered, onClick: (c: Ctx) => {
            const n = c.editor!.state.doc.nodeAt(f.pos); if (n) c.editor!.view.dispatch(c.editor!.state.tr.setNodeMarkup(f.pos, undefined, { ...n.attrs, numbered: !n.attrs.numbered }));
          } },
          { id: 'math-where-ctx', label: 'Экспликация «где»…', icon: 'list', onClick: async (c: Ctx) => {
            const r3 = await c.openDialog<any[]>('math.where', { latex, where: f.attrs.where || [], pos: f.pos, autofill: true });
            const n = c.editor!.state.doc.nodeAt(f.pos);
            if (r3 && n) c.editor!.view.dispatch(c.editor!.state.tr.setNodeMarkup(f.pos, undefined, { ...n.attrs, where: r3 }));
          } },
          { id: 'math-to-inline', label: 'Сделать строчной', onClick: (c: Ctx) => replaceFormula(c.editor, f.pos, 'formula', { latex, numbered: false, where: [], display: false }) },
        ] : [
          { id: 'math-to-block', label: 'Вынести в отдельную строку', onClick: (c: Ctx) => replaceFormula(c.editor, f.pos, 'mathInline', { latex, numbered: true, where: [], display: true }) },
        ]),
        { separator: true },
        { id: 'math-copy-word', label: 'Копировать для Word (MathML)', icon: 'copy', onClick: (c) => copyText(c, toMathML(latex, display), 'формула для Word') },
        { id: 'math-copy-latex', label: 'Копировать LaTeX', icon: 'copy', onClick: (c) => copyText(c, latex, 'LaTeX') },
        { id: 'math-copy-linear', label: 'Копировать линейную запись', icon: 'copy', onClick: (c) => copyText(c, L.latexToLinear(latex), 'линейная запись') },
      ];
    }
    if (fig) return [{ id: 'math-retype', label: 'Это формула-картинка - набрать формулой…', icon: 'formula', onClick: (c) => void retypeFigure(c, fig.pos) }];
    if (!t.selectionEmpty) return [{ id: 'math-conv-sel', label: 'Преобразовать в формулу', icon: 'formula', shortcut: 'Alt-=', onClick: (c) => convertSelection(c) }];
    return null;
  } });

  setupPaste(r);
  setupLint(r);

  // --- для других функций и автотестов: чистые преобразователи
  (window as any).stoMath = {
    canon: L.canon, symbols: L.symbols, hints: L.hints, isBulky: L.isBulky, latexToLinear: L.latexToLinear, chemToLatex: L.chemToLatex,
    breakPoints: L.breakPoints, breakAt: L.breakAt, anyToLatex: D.anyToLatex, detectFormat: D.detectFormat, convertLinear,
    ommlToLatex: O.ommlToLatex, mathmlToLatex: O.mathmlToLatex, convertWordEquations: O.convertWordEquations, renderTex, toMathML,
    ui: mathUI,
  };
}
